import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { Icon, Text } from '@/design-system'
import { Logo } from '@/app/Logo'
import { cn } from '@/lib/cn'
import styles from './FlowLayout.module.css'

export interface FlowLayoutProps {
  readonly step: number
  readonly totalSteps: number
  readonly back?: string | undefined
  readonly footer: ReactNode
  readonly children: ReactNode
}

/**
 * The shell every multi-step flow shares: progress, a way back, and a footer
 * that stays reachable on a phone without covering the content.
 */
export function FlowLayout({ step, totalSteps, back, footer, children }: FlowLayoutProps) {
  const navigate = useNavigate()
  return (
    <div className={styles.shell}>
      <div className={styles.bar}>
        <button
          type="button"
          onClick={() => (back ? navigate(back) : navigate(-1))}
          aria-label="Go back"
        >
          <Icon name="arrow_back" size={20} />
        </button>
        <div className={styles.steps} role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={totalSteps}>
          {Array.from({ length: totalSteps }, (_, index) => (
            <span
              key={index}
              className={cn(
                styles.step,
                index + 1 < step && styles.stepDone,
                index + 1 === step && styles.stepCurrent,
              )}
            />
          ))}
        </div>
        <Text variant="eyebrow" weight="semibold" tone="secondary">
          Step {step} of {totalSteps}
        </Text>
        <Logo size={18} className="pb-hide-sm" />
      </div>

      <div className={styles.body}>{children}</div>

      <div className={styles.footer}>
        <div className={styles.footerInner}>{footer}</div>
      </div>
    </div>
  )
}
